import React, { FunctionComponent, useState, useEffect, useRef } from "react";
import { Select, Modal } from "antd";
import {
    IChartMergedDatapoint,
    IGPSIndexDatapoint,
} from "../../../Models/Models";
import { DropdownComparisonWeight } from "../../../Enums/Enums";
import {
    RechartBarLine,
    ChartBodyWrapper,
    ChartButtonWrapper,
    ReportDataWrapper,
    formatYValue,
    ChartTitle,
    shortenStr,
} from "..";
import { FiZoomIn } from "@react-icons/all-files/fi/FiZoomIn";
import { FiZoomOut } from "@react-icons/all-files/fi/FiZoomOut";
import { RenderType } from "../../../Enums/RenderType";
import { featureFlags } from "../../../Common/CurrentFeatureFlags";
import {
    isOutputSelected,
    registerElement,
    registerRef,
} from "../DigitalShare/DigitalShareGenerator";
import { DigitalShareItemType } from "../../../Enums/DigitalShareItemType";
import { DigitalShareAppType } from "../../../Enums/DigitalShareAppType";
const { Option } = Select;

export interface IReportChartGroup {
    data?: IChartMergedDatapoint[];
    title?: string;
    nationalAverageLine?: boolean;
    primaryColor?: string;
    chartAnim?: string;
    scrollableParentSelector?: string;
    initialYValue?:
    | "nationalWeight"
    | "selection1PrimaryWeight"
    | "selection1SecondaryWeight";
    tooltipText?: string;
    tooltipText2?: string;
    secondaryColor?: string;
    xTickFormatter?: (id: string) => string;
    yValueTickFormatter?: (num: number) => string;
    verticalXAxis?: boolean;
    chartHeight?: number;
    makeXLabelsFitDemmit?: boolean;
    xAxisHeight?: number;
    primaryWeightLabel?: string;
    secondaryWeightLabel?: string;
    customChart?: JSX.Element;
    unprocessedTextAboveChart?: JSX.Element;
    makeTheCardHeightMatchDemmit?: boolean;
    selection1Name?: string;
    selection2Name?: string;
    customGroupingDropdowns?: string[][];
    customDropdownContent?: JSX.Element;
    dropdownHandlers?: ((str: string) => void)[];
    ignoreXTickFormatterOnTooltip?: boolean;
    sortByValue?: boolean;
    renderType?: RenderType;
    app?: DigitalShareAppType;
    itemType?: DigitalShareItemType;
    shareText?: (...args: any[]) => JSX.Element | string;
}

const _ReportChartGroup: FunctionComponent<IReportChartGroup> = (
    props: IReportChartGroup
) => {
    const [bar1ValueAttr, setBar1ValueAttr] = useState<
        "selection1PrimaryWeight" | "selection1SecondaryWeight" | "nationalWeight"
    >(props.initialYValue);
    const [bar2ValueAttr, setBar2ValueAttr] = useState<
        "selection2PrimaryWeight" | "selection2SecondaryWeight" | "nationalWeight"
    >(
        props.initialYValue === "selection1PrimaryWeight"
            ? "selection2PrimaryWeight"
            : "selection2SecondaryWeight"
    );
    const [topSel1NatObj, setTopSel1NatObj]
        = useState<IChartMergedDatapoint>(null);
    const [topSel1Sel2Obj, setTopSel1Sel2Obj]
        = useState<IChartMergedDatapoint>(null);
    const [tooltipText, setTooltipText] = useState<string>("");
    const [titleText, setTitleText] = useState<string>("");
    const [initialLoadComplete, setInitialLoadComplete]
        = useState<boolean>(false);
    const [sel1NatIndexValues, setSel1NatIndexValues]
        = useState<IGPSIndexDatapoint[]>(null);
    const [sel1Sel2IndexValues, setSel1Sel2IndexValues]
        = useState<IGPSIndexDatapoint[]>(null);
    const [visibleModal, setVisibleModal] = useState<boolean>(false);
    const [dropdownValue, setDropdownValue] = useState<number>(
        DropdownComparisonWeight.PRIMARY
    );
    const maxStrLen = 20;

    const renderType
        = props.renderType === undefined ? RenderType.DEFAULT : props.renderType;

    useEffect(() => {
        if (props.customChart) {
            setInitialLoadComplete(true);
            setTitleText(props.title);
        }
    }, []);

    useEffect(() => {
        // ERY: after initial load, if I update any dropdown, it re-renders 3-4 times. I'm sure it has to do with the order in which each
        // hook is happening, but I'm not quite sure how to overcome this. Would like a second set of eyes to make this smooth.

        // ERY: what I probably need to do here is only set a hook once everything is processed and once that's complete, then show the whole chart.
        // Might be word, but might work.
        if (bar1ValueAttr && props.data && props.data[0]) {
            props.data[0].nationalWeight
                ? setSel1NatIndexValues(calcAndSortIndexValues("Sel1Nat"))
                : setTopSel1NatObj(findGreatestObj("Sel1Nat"));

            if (props.data[0].selection2PrimaryWeight !== null) {
                setSel1Sel2IndexValues(calcAndSortIndexValues("Sel1Sel2"));
                setTopSel1Sel2Obj(findGreatestObj("Sel1Sel2"));
            }
        }
    }, [bar1ValueAttr]);

    useEffect(() => {
        if (sel1NatIndexValues) {
            setTopSel1NatObj(findGreatestObj("Sel1Nat"));
        }
    }, [sel1NatIndexValues]);

    useEffect(() => {
        if (sel1Sel2IndexValues) {
            setTopSel1Sel2Obj(findGreatestObj("Sel1Sel2"));
        }
    }, [sel1Sel2IndexValues]);

    useEffect(() => {
        if (topSel1NatObj && topSel1NatObj.id !== null) {
            setTitleText(formatTitleText(props.title));
            if (
                props.tooltipText
                && props.tooltipText2
                && props.data[0].selection2PrimaryWeight
            ) {
                setTooltipText(
                    formatTitleText(`${props.tooltipText2} ${props.tooltipText}`)
                );
            } else if (props.tooltipText) {
                setTooltipText(formatTitleText(props.tooltipText));
            }
            if (!initialLoadComplete) {
                setInitialLoadComplete(true);
            }
        }
    }, [topSel1NatObj, props.tooltipText, props.tooltipText2]);

    const handleVisitsChange = (value: DropdownComparisonWeight) => {
        switch (value) {
            case DropdownComparisonWeight.PRIMARY:
                setBar1ValueAttr(`selection1PrimaryWeight`);
                setBar2ValueAttr(`selection2PrimaryWeight`);
                setDropdownValue(DropdownComparisonWeight.PRIMARY);
                break;
            case DropdownComparisonWeight.SECONDARY:
                setBar1ValueAttr(`selection1SecondaryWeight`);
                setBar2ValueAttr(`selection2SecondaryWeight`);
                setDropdownValue(DropdownComparisonWeight.SECONDARY);
                break;
        }
    };

    // ███████╗ ██████╗ ██████╗ ████████╗
    // ██╔════╝██╔═══██╗██╔══██╗╚══██╔══╝
    // ███████╗██║   ██║██████╔╝   ██║
    // ╚════██║██║   ██║██╔══██╗   ██║
    // ███████║╚██████╔╝██║  ██║   ██║
    // ╚══════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝

    /**
   * This method is used to find the greatest value object based on if there is an index calculation or not. If there is
   * no index calcuation, it sorts the array by greatest overall value.
   */
    // TODO ERY: need to abstract this to find either the greatest sel1 obj or sel2 obj
    const findGreatestObj = (
        type: "Sel1Nat" | "Sel1Sel2"
    ): IChartMergedDatapoint => {
        let greatestObj: IChartMergedDatapoint;
        if (type === "Sel1Nat" && sel1NatIndexValues) {
            greatestObj = (props.data as IChartMergedDatapoint[]).find(
                (act: IChartMergedDatapoint) => act.id === sel1NatIndexValues[0].id
            );
        } else if (type === "Sel1Sel2" && sel1Sel2IndexValues) {
            greatestObj = (props.data as IChartMergedDatapoint[]).find(
                (act: IChartMergedDatapoint) => act.id === sel1Sel2IndexValues[0].id
            );
        } else {
            greatestObj = greatestSel1Obj();
        }
        return { ...greatestObj };
    };

    // ERY: gettin' pretty frail...lots of exceptions
    const greatestSel1ValueSort = (
        a: IChartMergedDatapoint,
        b: IChartMergedDatapoint
    ) => {
        if (bar1ValueAttr === `selection1PrimaryWeight`) {
            return b.selection1PrimaryWeight - a.selection1PrimaryWeight;
        } else if (bar1ValueAttr === `selection1SecondaryWeight`) {
            return b.selection1SecondaryWeight - a.selection1SecondaryWeight;
        } else {
            return b.nationalWeight - a.nationalWeight;
        }
    };

    const greatestSel1Obj = (): IChartMergedDatapoint => {
        const dataCopy = [...props.data];
        return dataCopy.sort(greatestSel1ValueSort)[0];
    };

    //  ██████╗ █████╗ ██╗      ██████╗██╗   ██╗██╗      █████╗ ████████╗███████╗
    // ██╔════╝██╔══██╗██║     ██╔════╝██║   ██║██║     ██╔══██╗╚══██╔══╝██╔════╝
    // ██║     ███████║██║     ██║     ██║   ██║██║     ███████║   ██║   █████╗
    // ██║     ██╔══██║██║     ██║     ██║   ██║██║     ██╔══██║   ██║   ██╔══╝
    // ╚██████╗██║  ██║███████╗╚██████╗╚██████╔╝███████╗██║  ██║   ██║   ███████╗
    //  ╚═════╝╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═════╝ ╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

    /**
   * Calculates the index value for either a selection 1 vs national weight comparison or a selection 1 vs selection 2 comparison.
   * @param type
   */
    const calcAndSortIndexValues = (type: "Sel1Nat" | "Sel1Sel2") => {
        let returnValue = null;
        if (props.data[0].selection2PrimaryWeight || props.data[0].nationalWeight) {
            const newIndexValues: IGPSIndexDatapoint[] = [];
            (props.data as Array<IChartMergedDatapoint>).forEach(
                (mergedAct: IChartMergedDatapoint) => {
                    const numerator
                        = bar1ValueAttr === "selection1PrimaryWeight"
                            ? mergedAct.selection1PrimaryWeight
                            : mergedAct.selection1SecondaryWeight;
                    let denominator: number;
                    if (type === "Sel1Nat") {
                        denominator = mergedAct.nationalWeight;
                    } else {
                        denominator
                            = bar2ValueAttr === "selection2PrimaryWeight"
                                ? mergedAct.selection2PrimaryWeight
                                : mergedAct.selection2SecondaryWeight;
                    }
                    newIndexValues.push({
                        id: mergedAct.id,
                        indexValue: numerator / denominator,
                    });
                }
            );
            if (newIndexValues.length > 0) {
                newIndexValues.sort((a, b) => b.indexValue - a.indexValue);
                returnValue = newIndexValues;
            }
        }
        return returnValue;
    };

    // ███████╗ ██████╗ ██████╗ ███╗   ███╗ █████╗ ████████╗
    // ██╔════╝██╔═══██╗██╔══██╗████╗ ████║██╔══██╗╚══██╔══╝
    // █████╗  ██║   ██║██████╔╝██╔████╔██║███████║   ██║
    // ██╔══╝  ██║   ██║██╔══██╗██║╚██╔╝██║██╔══██║   ██║
    // ██║     ╚██████╔╝██║  ██║██║ ╚═╝ ██║██║  ██║   ██║
    // ╚═╝      ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝╚═╝  ╚═╝   ╚═╝

    const specificObj = (
        data: IChartMergedDatapoint[],
        sortStr: string
    ): IChartMergedDatapoint => {
        const returnVal = (data as IChartMergedDatapoint[]).find(
            (d: IChartMergedDatapoint) => d.id === sortStr
        );

        for (const property in returnVal) {
            if (property !== "id") {
                if (returnVal[property] === null || returnVal[property] === undefined) {
                    returnVal[property] = 0;
                }
            }
        }

        return returnVal;
    };

    const specificValue = (
        data: IChartMergedDatapoint[],
        sortStr: string,
        valName: string
    ): number => {
        const mergedObj = specificObj(data, sortStr);
        return mergedObj[valName];
    };

    const whichSel1Value = (obj: IChartMergedDatapoint): number => {
        return bar1ValueAttr === `selection1PrimaryWeight`
            ? obj?.selection1PrimaryWeight
            : obj?.selection1SecondaryWeight;
    };

    const whichSel2Value = (obj: IChartMergedDatapoint): number => {
        return bar2ValueAttr === `selection2PrimaryWeight`
            ? obj?.selection2PrimaryWeight
            : obj?.selection2SecondaryWeight;
    };

    const higherLowerEqual = (
        sortStr: string,
        val1Name: string,
        val2Name: string
    ): string => {
        const mergedObj = specificObj(props.data, sortStr);

        let mergedObjVal1: number = mergedObj[val1Name];
        if (!mergedObjVal1 || isNaN(mergedObjVal1) || typeof mergedObjVal1 !== 'number') {
            mergedObjVal1 = 0;
        }

        let mergedObjVal2: number = mergedObj[val2Name];
        if (!mergedObjVal2 || isNaN(mergedObjVal2) || typeof mergedObjVal2 !== 'number') {
            mergedObjVal2 = 0;
        }
        const compareValue1 = mergedObjVal1.toFixed(4);
        const compareValue2 = mergedObjVal2.toFixed(4);
        let returnString: string;
        if (compareValue1 === compareValue2) {
            returnString = "equal to";
        } else if (compareValue1 > compareValue2) {
            returnString = "higher than";
        } else {
            returnString = "lower than";
        }
        return returnString;
    };

    const formatTitleText = (str: string): string => {
        return str.replace(/\[(.+?)\]/g, titleTextReplacer);
    };

    const titleTextReplacer = (match: string): string => {
        let replacement: string;
        switch (match) {
            case "[marriageSel1NatHigherLower]":
                replacement = higherLowerEqual(
                    "Married",
                    bar1ValueAttr,
                    "nationalWeight"
                );
                break;
            case "[marriageSel1Sel2HigherLower]":
                replacement = higherLowerEqual("Married", bar1ValueAttr, bar2ValueAttr);
                break;
            case "[marriageSel1Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Married", bar1ValueAttr)
                );
                break;
            case "[marriageSel2Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Married", bar2ValueAttr)
                );
                break;
            case "[marriageNationalValue]":
                replacement = formatYValue(
                    specificValue(props.data, "Married", "nationalWeight")
                );
                break;
            case "[childrenSel1NatHigherLower]":
            case "[newMoverSel1NationalHigherLower]":
            case "[newOwnerSel1NationalHigherLower]":
                replacement = higherLowerEqual("Y", bar1ValueAttr, "nationalWeight");
                break;
            case "[childrenSel1Sel2HigherLower]":
            case "[newMoverSel1Sel2HigherLower]":
            case "[newOwnerSel1Sel2HigherLower]":
                replacement = higherLowerEqual("Y", bar1ValueAttr, bar2ValueAttr);
                break;
            case "[childrenSel1Value]":
            case "[newOwnerSel1Value]":
            case "[newMoverSel1Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Y", bar1ValueAttr)
                );
                break;
            case "[childrenSel2Value]":
            case "[newOwnerSel2Value]":
            case "[newMoverSel2Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Y", bar2ValueAttr)
                );
                break;
            case "[childrenNationalValue]":
            case "[newOwnerNationalValue]":
            case "[newMoverNationalValue]":
                replacement = formatYValue(
                    specificValue(props.data, "Y", "nationalWeight")
                );
                break;
            case "[dwellingTypeSel1NatHigherLower]":
                replacement = higherLowerEqual(
                    "Single Family Dwelling Unit",
                    bar1ValueAttr,
                    "nationalWeight"
                );
                break;
            case "[dwellingTypeNatValue]":
                replacement = formatYValue(
                    specificValue(
                        props.data,
                        "Single Family Dwelling Unit",
                        "nationalWeight"
                    )
                );
                break;
            case "[dwellingTypeSel1Sel2HigherLower]":
                replacement = higherLowerEqual(
                    "Single Family Dwelling Unit",
                    bar1ValueAttr,
                    bar2ValueAttr
                );
                break;
            case "[ownSel1Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Own", bar1ValueAttr)
                );
                break;
            case "[ownSel1NatHigherLower]":
                replacement = higherLowerEqual("Own", bar1ValueAttr, "nationalWeight");
                break;
            case "[ownSel1Sel2HigherLower]":
                replacement = higherLowerEqual("Own", bar1ValueAttr, bar2ValueAttr);
                break;
            // Rent vs Own Sel 2 Value
            case "[ownSel2Value]":
                replacement = formatYValue(
                    specificValue(props.data, "Own", bar2ValueAttr)
                );
                break;
            case "[ownNationalValue]":
                replacement = formatYValue(
                    specificValue(props.data, "Own", "nationalWeight")
                );
                break;
            case "[personPeopleSel1Nat]":
                replacement = Number(topSel1NatObj?.id) > 1 ? "people" : "person";
                break;
            case "[personPeopleSel1Sel2]":
                replacement = Number(topSel1Sel2Obj?.id) > 1 ? "people" : "person";
                break;
            case "[greatestOwnSel1Id]":
                replacement
                    = greatestSel1Obj().id === "Unknown"
                        ? "are Unknown"
                        : greatestSel1Obj().id;
                break;
            case "[greatestSel1Id]":
                replacement = props.xTickFormatter
                    ? props.xTickFormatter(greatestSel1Obj().id)
                    : greatestSel1Obj().id;
                break;
            case "[greatestSel1Value]":
                replacement = formatYValue(whichSel1Value(greatestSel1Obj()));
                break;
            // Greatest Selection 1 obj's Selection 2 Value
            case "[greatestSel1CompSel2Value]":
                replacement = formatYValue(whichSel1Value(greatestSel1Obj()));
                break;
            // Selection 1 vs National Top ID
            case "[sel1NatTopId]":
                replacement = props.xTickFormatter
                    ? props.xTickFormatter(topSel1NatObj.id)
                    : topSel1NatObj.id;
                break;
            // Selection 1 vs National Selection 1 Value
            case "[sel1NatCompSel1TopValue]":
                replacement = formatYValue(whichSel1Value(topSel1NatObj));
                break;
            // Selection 1 vs National National Value
            case "[sel1NatCompNatTopValue]":
                replacement = formatYValue(
                    props.data.find((obj) => obj.id === topSel1NatObj.id).nationalWeight
                );
                break;
            // Selection 1 vs Selection 2 Top ID
            case "[sel1Sel2TopId]":
                replacement = props.xTickFormatter
                    ? props.xTickFormatter(topSel1Sel2Obj?.id)
                    : topSel1Sel2Obj?.id;
                break;
            // Selection 1 vs Selection 2 Selection 1 Value
            case "[sel1Sel2CompSel1TopValue]":
                replacement = formatYValue(whichSel1Value(topSel1Sel2Obj));
                break;
            // Selection 1 vs Selection 2 Selection 2 Value
            case "[sel1Sel2CompSel2TopValue]":
                replacement = formatYValue(whichSel2Value(topSel1Sel2Obj));
                break;
            case "[visitVisitor]":
                replacement
                    = bar1ValueAttr === `selection1SecondaryWeight` ? "Visit" : "Visitor";
                break;
            default:
                replacement = match;
                break;
        }
        return replacement;
    };

    const InteractionArea: JSX.Element
        = renderType === RenderType.DIGITAL_SHARE
            || renderType === RenderType.PRINT ? null : (
            <ChartButtonWrapper>
                <div
                    style={props.makeTheCardHeightMatchDemmit ? { height: "32px" } : null}
                >
                    {props.primaryWeightLabel
                        && props.secondaryWeightLabel
                        && props.data
                        && props.data[0]
                        && props.data[0].selection1SecondaryWeight ? (
                        <Select
                            defaultValue={dropdownValue}
                            value={dropdownValue}
                            onChange={handleVisitsChange}
                        >
                            <Option value={DropdownComparisonWeight.PRIMARY}>
                                {shortenStr(props.primaryWeightLabel, maxStrLen)}
                            </Option>
                            <Option value={DropdownComparisonWeight.SECONDARY}>
                                {shortenStr(props.secondaryWeightLabel, maxStrLen)}
                            </Option>
                        </Select>
                    ) : null}
                    {props.customGroupingDropdowns
                        && props.dropdownHandlers
                        && props.dropdownHandlers.map((ddHandler, i) => (
                            <Select
                                key={`dropdownHandlers-${i}`}
                                defaultValue={shortenStr(
                                    props.customGroupingDropdowns[i][0],
                                    maxStrLen
                                )}
                                onChange={ddHandler}
                                style={{ marginLeft: "10px" }}
                            >
                                {props.customGroupingDropdowns[i].map((str) => (
                                    <Option
                                        key={`customGroupingVal-${i}-${str}`}
                                        value={str.toLowerCase()}
                                    >
                                        {shortenStr(str, maxStrLen)}
                                    </Option>
                                ))}
                            </Select>
                        ))}
                    {props.customDropdownContent}
                </div>
                {!visibleModal ? (
                    <FiZoomIn
                        size={16}
                        color="#2e3d49"
                        onClick={() => {
                            setVisibleModal(true);
                        }}
                    />
                ) : (
                    <FiZoomOut
                        size={16}
                        color="#2e3d49"
                        onClick={() => setVisibleModal(false)}
                    />
                )}
            </ChartButtonWrapper>
        );

    const sortedData = props.sortByValue
        ? props.data.sort(greatestSel1ValueSort)
        : props.data;

    const ref = useRef<HTMLDivElement>(null);

    const Chart: JSX.Element = props.customChart ? (
        <>{props.customChart}</>
    ) : (
        <>
            {InteractionArea}
            <div
                data-testclass={"report-chart-group-wrapper"}
                onClick={() => {
                    setVisibleModal(true);
                }}
            >
                <RechartBarLine
                    initialYValue={props.initialYValue}
                    yValueTickFormatter={formatYValue}
                    xTickFormatter={props.xTickFormatter}
                    bar1color={props.primaryColor}
                    bar2color={props.secondaryColor}
                    data={sortedData}
                    bar1ValueAttr={bar1ValueAttr}
                    bar2ValueAttr={bar2ValueAttr}
                    verticalXAxis={props.verticalXAxis}
                    chartHeight={props.chartHeight}
                    makeXLabelsFitDemmit={props.makeXLabelsFitDemmit}
                    xAxisHeight={props.xAxisHeight}
                    selection1Name={props.selection1Name}
                    selection2Name={props.selection2Name}
                    primaryWeightLabel={props.primaryWeightLabel}
                    secondaryWeightLabel={props.secondaryWeightLabel}
                    ignoreXTickFormatterOnTooltip={props.ignoreXTickFormatterOnTooltip}
                    disableAnimation={props.renderType !== RenderType.DEFAULT}
                />
            </div>
        </>
    );

    if (
        featureFlags.DigitalShare
        && renderType === RenderType.DIGITAL_SHARE
        && isOutputSelected(props.app, props.itemType)
    ) {
        if (initialLoadComplete) {
            const text = props.shareText(dropdownValue);
            registerRef(props.app.toString(), props.itemType.toString(), ref);
            registerElement(
                props.app.toString(),
                props.itemType.toString(),
                text,
                Chart
            );
        }
    }

    return (
        <ReportDataWrapper data-testclass="report-chart-group-wrapper">
            <ChartTitle titleText={titleText} tooltipText={tooltipText} />
            {props.unprocessedTextAboveChart}
            <ChartBodyWrapper
                scrollableParentSelector={props.scrollableParentSelector}
                animateIn={props.chartAnim}
                offset={100}
                animateOnce={true}
            >
                {initialLoadComplete && Chart}
            </ChartBodyWrapper>
            <Modal
                title=""
                visible={visibleModal}
                onCancel={() => {
                    setVisibleModal(false);
                }}
                footer={null}
                width="90%"
                centered={true}
                bodyStyle={{ padding: "40px 24px 24px" }}
            >
                {initialLoadComplete && Chart}
            </Modal>
        </ReportDataWrapper>
    );
};

export const ReportChartGroup = React.memo(_ReportChartGroup);
